/**
 * The default registration type is represented as an empty UUID/GUID. For all events whether
 * registration types are enabled or not the default registration type will exist.
 */
export const defaultRegistrationTypeId = '00000000-0000-0000-0000-000000000000';

/**
 * Converts an array of full registration type objects to an array of objects with
 * id and name attributes only (make it lighter) and also sorts the array alphebatcially
 * using locale.
 * @param {object[]} eventRegistrationTypes
 * @param {string} locale
 */
export function mapEventRegistrationTypesSorted(eventRegistrationTypes, locale) {
  if (!eventRegistrationTypes) { return []; }
  return Object.values(eventRegistrationTypes)
    .filter(type => type.id !== defaultRegistrationTypeId)
    .map(regType => ({ id: regType.id, text: regType.name }))
    .sort((a, b) => a.text.localeCompare(b.text, locale, { sensitivity: 'base' }));
}

/**
 * Add additional elements to the front of the source array. Any element
 * already existed or added is excluded.
 * @param {string[]} sourceArray
 * @param {...string} elementsToAdd
 * @returns {Array}
 */
const unshiftStringArrayMinusDuplication = (sourceArray = [], ...elementsToAdd) => {
  const resultArray = [...sourceArray];
  elementsToAdd.forEach(entry => {
    if (entry && !resultArray.includes(entry)) { // put in front, without duplicates
      resultArray.unshift(entry);
    }
  });
  return resultArray;
};

/**
 * Returns an array of registraiton types, with only id and name attributes, taking
 * into consideration of planner limiting registration type visibility and forcing
 * display of certain registration types, such as a guest coming to guest site
 * with a precoded link where the corresponding registration type would not be visible
 * based on planner registration type visibility settings.
 * @param {object[]} eventRegistrationTypes - all registration types available for the event
 * @param {object} registrationTypeSettings - standard settings object for planner reg type configuration
 * @param {...string} forcedVisibleRegistrationTypeIds - additional registration types must be visible
 * @returns {object[]} of format {id: string, text: string}
 */
export const getVisibleRegistrationTypes = (
  eventRegistrationTypes,
  registrationTypeSettings,
  ...forcedVisibleRegistrationTypeIds
) => {
  if (!eventRegistrationTypes || !registrationTypeSettings) {
    return [];
  }

  if (!registrationTypeSettings.limitVisibility) {
    return mapEventRegistrationTypesSorted(eventRegistrationTypes);
  }

  const visibleRegistrationTypeIds = registrationTypeSettings.categorizedRegistrationTypes;

  return unshiftStringArrayMinusDuplication(visibleRegistrationTypeIds, ...forcedVisibleRegistrationTypeIds)
    .filter(id => eventRegistrationTypes[id]) // removed ones deleted from backend service (Normandy)
    .map(id => ({ id, text: eventRegistrationTypes[id].name }));
};
